import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeftRight,
  Award,
  BookOpen,
  Megaphone,
  Copy,
  Activity,
  LogOut,
  MessageSquare,
  Palette,
  Settings,
  Shield,
  Store,
  Users,
} from 'lucide-react';

import { PlatformLogo } from '@/components/PlatformLogo';
import {
  Sidebar,
  SidebarMenu,
  useSidebar,
} from '@/components/ui/sidebar';
import { useToast } from '@/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ThemeModeMenu } from '@/components/theme/ThemeModeMenu';
import { formatClipboardJson } from '@/lib/clipboardJson';
import { AppCommandPalette } from './AppCommandPalette';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useVendor } from '@/contexts/VendorContext';
import { useAppShellSummary } from '@/hooks/useAppShellSummary';
import { useCurrentUserProfile } from '@/hooks/useCurrentUserProfile';
import { appBuildInfo, formatBuildInfoForClipboard, formatBuiltAtForDisplay, formatVersionForDisplay } from '@/lib/buildInfo';
import { getProfileDisplayName, getProfileInitials, hasProfileDisplayIdentity } from '@/lib/profileDisplay';
import { beginPortalSwitch } from '@/lib/portalSwitch';
import {
  ADMIN_PROFILE_LABEL,
  CUSTOMER_PROFILE_LABEL,
  MSP_PROFILE_LABEL,
  SUPPLIER_PROFILE_LABEL,
  SWITCH_PROFILE_LABEL,
} from '@/lib/surfaceViewLabel';
import { getAdminNavigationItems } from '@/lib/adminNavigation';
import { getAdminActiveTab, getAdminTabUrl } from '@/lib/adminTabs';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { FEATURE_FLAG_KEYS } from '@/lib/featureFlagsCatalog';
import {
  setSuperAdminDebugOverlayEnabled,
  useSuperAdminDebugOverlayPreference,
} from '@/lib/superAdminDebugOverlayPreference';
import {
  SurfaceSidebarBackdrop,
  SurfaceSidebarBrandButton,
  SurfaceSidebarCopyButton,
  SurfaceSidebarBrandPanel,
  SurfaceSidebarContent,
  SurfaceSidebarFooter,
  SurfaceSidebarHeader,
  SurfaceSidebarNavItemLink,
  SurfaceSidebarPortalLink,
  SurfaceSidebarProfileMenu,
  SurfaceSidebarSection,
  SurfaceSidebarSwitcherControl,
  SurfaceSidebarSwitcherButton,
} from './SurfaceSidebar';

// Build-time flag that mirrors the per-workflow `BUILD_STORYBOOK=1`
// signal driving `scripts/maybe-build-storybook.mjs`. Set on preview +
// staging Vercel builds, unset on the live release deploy so the
// Storybook nav entry stays out of production where the catalog isn't
// actually deployed (codex P2 on bcf8026cc — admins were seeing a link
// that landed on a 503 gate-not-configured response). See
// docs/storybook.md "Environment configuration".
const storybookCatalogAvailable =
  (import.meta.env.VITE_STORYBOOK_AVAILABLE ?? "").toString().toLowerCase() === "1" ||
  (import.meta.env.VITE_STORYBOOK_AVAILABLE ?? "").toString().toLowerCase() === "true";

export function AdminSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, organization } = useOrganization();
  const { contextVendorProfile, contextClaimableProfiles, contextDomain } = useVendor();
  const { currentUserProfile: profile } = useAppShellSummary({
    organizationId: organization?.id,
    userId: user?.id,
    deferUntilIdle: true,
  });
  const directProfile = useCurrentUserProfile(user?.id);
  const resolvedProfile = hasProfileDisplayIdentity(directProfile) ? directProfile : profile;
  const [searchParams] = useSearchParams();
  const debugOverlayEnabled = useSuperAdminDebugOverlayPreference();

  const handleSignOut = async () => {
    const { signOutGlobally } = await import('@/lib/auth');
    await signOutGlobally('/auth', true);
  };

  const { enabled: isScanEngineSettingsEnabled } = useFeatureFlag(
    FEATURE_FLAG_KEYS.scanEngineSettings,
    { userId: user?.id },
  );
  const { enabled: isScanEngineOperationsEnabled } = useFeatureFlag(
    FEATURE_FLAG_KEYS.scanEngineOperations,
    { userId: user?.id },
  );
  const { enabled: isCanonicalLogoProcessingEnabled } = useFeatureFlag(
    FEATURE_FLAG_KEYS.canonicalLogoProcessing,
    { userId: user?.id },
  );
  const adminNavItems = getAdminNavigationItems({
    enabledFeatureFlags: {
      [FEATURE_FLAG_KEYS.scanEngineSettings]: isScanEngineSettingsEnabled,
      [FEATURE_FLAG_KEYS.scanEngineOperations]: isScanEngineOperationsEnabled,
      [FEATURE_FLAG_KEYS.canonicalLogoProcessing]: isCanonicalLogoProcessingEnabled,
    },
  });
  const activeTab = getAdminActiveTab(searchParams.get('tab'));
  const hasWorkspaceSupplierContext = Boolean(contextVendorProfile)
    || Boolean(contextClaimableProfiles.length);
  const orgSettings = organization?.settings as { domain?: string } | null;
  const supplierClaimDomain = hasWorkspaceSupplierContext
    ? (contextDomain || orgSettings?.domain?.toLowerCase() || organization?.slug?.toLowerCase())
    : null;
  const supplierPortalUrl = hasWorkspaceSupplierContext
    ? contextVendorProfile
      ? '/vendor'
      : `/vendor/claim${supplierClaimDomain ? `?domain=${supplierClaimDomain}` : ''}`
    : null;
  const supplierPortalLabel = contextVendorProfile
    ? SUPPLIER_PROFILE_LABEL
    : 'Claim Supplier Profile';
  const commandPaletteItems = useMemo(
    () => [
      ...adminNavItems.map((item) => ({
        id: `admin-${item.tab}`,
        kind: 'page' as const,
        title: item.title,
        subtitle: item.description,
        pathLabel: `Page > Admin > ${item.title}`,
        url: item.url,
        icon: item.icon,
        section: 'Admin' as const,
        keywords: ['admin', item.title.toLowerCase()],
      })),
      {
        id: 'admin-accounts-users',
        kind: 'page' as const,
        title: 'Users',
        subtitle: 'Recent signups, MFA, and account access',
        pathLabel: 'Page > Admin > Accounts > Users',
        url: getAdminTabUrl('accounts', 'users'),
        icon: Users,
        section: 'Admin' as const,
        keywords: ['admin', 'accounts', 'users', 'signups', 'mfa'],
      },
      {
        id: 'admin-accounts-organisations',
        kind: 'page' as const,
        title: 'Organisations',
        subtitle: 'Organisation records and membership access',
        pathLabel: 'Page > Admin > Accounts > Organisations',
        url: getAdminTabUrl('accounts', 'organisations'),
        icon: Users,
        section: 'Admin' as const,
        keywords: ['admin', 'accounts', 'organisations', 'organizations', 'tenants'],
      },
      {
        id: 'admin-accounts-supplierorgs',
        kind: 'page' as const,
        title: 'SupplierOrgs',
        subtitle: 'Verified supplier organisations and account management',
        pathLabel: 'Page > Admin > Accounts > SupplierOrgs',
        url: getAdminTabUrl('accounts', 'supplierorgs'),
        icon: Users,
        section: 'Admin' as const,
        keywords: ['admin', 'accounts', 'supplierorgs', 'supplier organisations', 'verified suppliers'],
      },
      {
        id: 'admin-accounts-msp',
        kind: 'page' as const,
        title: 'MSP',
        subtitle: 'Managed service provider accounts',
        pathLabel: 'Page > Admin > Accounts > MSP',
        url: getAdminTabUrl('accounts', 'msp'),
        icon: Users,
        section: 'Admin' as const,
        keywords: ['admin', 'accounts', 'msp', 'provider', 'managed'],
      },
      {
        id: 'admin-certificates-all',
        kind: 'page' as const,
        title: 'All Certificates',
        subtitle: 'Full platform certificate registry',
        pathLabel: 'Page > Admin > Certificates > All Certificates',
        url: getAdminTabUrl('certificates', 'all'),
        icon: Award,
        section: 'Admin' as const,
        keywords: ['admin', 'certificates', 'registry', 'all certificates'],
      },
      {
        id: 'admin-certificates-ce',
        kind: 'page' as const,
        title: 'Cyber Essentials',
        subtitle: 'Cyber Essentials registry and uploads',
        pathLabel: 'Page > Admin > Certificates > Cyber Essentials',
        url: getAdminTabUrl('certificates', 'cyber-essentials'),
        icon: Shield,
        section: 'Admin' as const,
        keywords: ['admin', 'certificates', 'cyber essentials', 'ce', 'ce+'],
      },
      {
        id: 'admin-certificates-logos',
        kind: 'page' as const,
        title: 'Certificate Logos',
        subtitle: 'Certificate logo assets and branding',
        pathLabel: 'Page > Admin > Certificates > Certificate Logos',
        url: getAdminTabUrl('certificates', 'logos'),
        icon: Award,
        section: 'Admin' as const,
        keywords: ['admin', 'certificates', 'logos', 'assets', 'branding'],
      },
      {
        id: 'admin-marketplace-listings',
        kind: 'page' as const,
        title: 'Listings',
        subtitle: 'Marketplace listings and supplier visibility',
        pathLabel: 'Page > Admin > Marketplace > Listings',
        url: getAdminTabUrl('marketplace', 'listings'),
        icon: Store,
        section: 'Admin' as const,
        keywords: ['admin', 'marketplace', 'listings', 'suppliers'],
      },
      {
        id: 'admin-marketplace-supplier-profile',
        kind: 'page' as const,
        title: 'Supplier Profile',
        subtitle: 'Marketplace supplier profile settings',
        pathLabel: 'Page > Admin > Marketplace > Supplier Profile',
        url: getAdminTabUrl('marketplace', 'supplier-profile'),
        icon: Store,
        section: 'Admin' as const,
        keywords: ['admin', 'marketplace', 'supplier profile', 'profile settings'],
      },
      {
        id: 'admin-marketplace-dependencies',
        kind: 'page' as const,
        title: 'Dependencies',
        subtitle: 'Marketplace dependency relationships',
        pathLabel: 'Page > Admin > Marketplace > Dependencies',
        url: getAdminTabUrl('marketplace', 'dependencies'),
        icon: Store,
        section: 'Admin' as const,
        keywords: ['admin', 'marketplace', 'dependencies', 'graph', 'network'],
      },
      {
        id: 'admin-marketplace-signatures',
        kind: 'page' as const,
        title: 'Signatures',
        subtitle: 'Technographic signature settings',
        pathLabel: 'Page > Admin > Marketplace > Signatures',
        url: getAdminTabUrl('marketplace', 'signatures'),
        icon: Store,
        section: 'Admin' as const,
        keywords: ['admin', 'marketplace', 'signatures', 'tech signatures'],
      },
      // Marketplace operational sub-views (switched via `marketplaceView`,
      // not `subtab`). These are the chip buttons inside the Listings tab.
      {
        id: 'admin-marketplace-view-data-health',
        kind: 'page' as const,
        title: 'Marketplace Data Health',
        subtitle: 'Marketplace data quality and coverage gaps',
        pathLabel: 'Page > Admin > Marketplace > Data Health',
        url: '/admin?tab=marketplace&marketplaceView=data-health',
        icon: Store,
        section: 'Admin' as const,
        keywords: ['admin', 'marketplace', 'data health', 'coverage', 'data quality'],
      },
      {
        id: 'admin-marketplace-view-impressions',
        kind: 'page' as const,
        title: 'Marketplace Impressions',
        subtitle: 'Listing impression and visibility data',
        pathLabel: 'Page > Admin > Marketplace > Impressions',
        url: '/admin?tab=marketplace&marketplaceView=impressions',
        icon: Store,
        section: 'Admin' as const,
        keywords: ['admin', 'marketplace', 'impressions', 'visibility', 'views'],
      },
      {
        id: 'admin-marketplace-view-queue-monitor',
        kind: 'page' as const,
        title: 'Queue Monitor',
        subtitle: 'Discovery and security scan queues',
        pathLabel: 'Page > Admin > Marketplace > Queue Monitor',
        url: '/admin?tab=marketplace&marketplaceView=queue-monitor',
        icon: Store,
        section: 'Admin' as const,
        keywords: ['admin', 'marketplace', 'queue monitor', 'discovery queue', 'scan queue'],
      },
      {
        id: 'admin-marketplace-view-onboarding-diagnostics',
        kind: 'page' as const,
        title: 'Onboarding Diagnostics',
        subtitle: 'Diagnose and repair affected onboarding outcomes',
        pathLabel: 'Page > Admin > Marketplace > Onboarding Diagnostics',
        url: '/admin?tab=marketplace&marketplaceView=onboarding-diagnostics',
        icon: Store,
        section: 'Admin' as const,
        keywords: ['admin', 'marketplace', 'onboarding diagnostics', 'affected suppliers', 'manual review', 'retry'],
      },
      {
        id: 'admin-marketplace-view-onboard-batch-monitor',
        kind: 'page' as const,
        title: 'Onboarding Batch Monitor',
        subtitle: 'Batch table + per-listing/domain/scan pipeline grid',
        pathLabel: 'Page > Admin > Marketplace > Onboarding Batch Monitor',
        url: '/admin?tab=marketplace&marketplaceView=onboard-batch-monitor',
        icon: Store,
        section: 'Admin' as const,
        keywords: ['admin', 'marketplace', 'onboarding batch monitor', 'onboard batch monitor', 'batch report', 'pipeline grid', 'onboarding batches'],
      },
      {
        id: 'admin-marketplace-view-onboarding-review',
        kind: 'page' as const,
        title: 'Onboarding Review Cockpit',
        subtitle: 'Triage stuck onboardings — resume scans, resolve redirects',
        pathLabel: 'Page > Admin > Marketplace > Onboarding Review Cockpit',
        url: '/admin?tab=marketplace&marketplaceView=onboarding-review',
        icon: Store,
        section: 'Admin' as const,
        keywords: ['admin', 'marketplace', 'onboarding review', 'review cockpit', 'canonical url', 'resume scans', 'manual review', 'score coverage'],
      },
      {
        id: 'admin-communications-emails',
        kind: 'page' as const,
        title: 'Emails',
        subtitle: 'Support contacts and email testing',
        pathLabel: 'Page > Admin > Communications > Emails',
        url: getAdminTabUrl('communications', 'emails'),
        icon: MessageSquare,
        section: 'Admin' as const,
        keywords: ['admin', 'communications', 'emails', 'support'],
      },
      {
        id: 'admin-communications-queue',
        kind: 'page' as const,
        title: 'Email Queue',
        subtitle: 'Queued and failed outbound email jobs',
        pathLabel: 'Page > Admin > Communications > Email Queue',
        url: getAdminTabUrl('communications', 'email-queue'),
        icon: MessageSquare,
        section: 'Admin' as const,
        keywords: ['admin', 'communications', 'email queue', 'queue'],
      },
      {
        id: 'admin-communications-invitations',
        kind: 'page' as const,
        title: 'Invitations',
        subtitle: 'Pending invitations and resend actions',
        pathLabel: 'Page > Admin > Communications > Invitations',
        url: getAdminTabUrl('communications', 'invitations'),
        icon: MessageSquare,
        section: 'Admin' as const,
        keywords: ['admin', 'communications', 'invitations', 'resend'],
      },
      {
        id: 'admin-communications-support-notices',
        kind: 'page' as const,
        title: 'Support Notices',
        subtitle: 'Platform support notice controls',
        pathLabel: 'Page > Admin > Communications > Support Notices',
        url: getAdminTabUrl('communications', 'support-notices'),
        icon: MessageSquare,
        section: 'Admin' as const,
        keywords: ['admin', 'communications', 'support notices', 'notices'],
      },
      {
        id: 'admin-content-questionnaires',
        kind: 'page' as const,
        title: 'Questionnaires',
        subtitle: 'Questionnaire management and publishing',
        pathLabel: 'Page > Admin > Content > Questionnaires',
        url: getAdminTabUrl('content', 'questionnaires'),
        icon: BookOpen,
        section: 'Admin' as const,
        keywords: ['admin', 'content', 'questionnaires', 'forms'],
      },
      {
        id: 'admin-content-glossary',
        kind: 'page' as const,
        title: 'Glossary',
        subtitle: 'Security glossary and shared terms',
        pathLabel: 'Page > Admin > Content > Glossary',
        url: getAdminTabUrl('content', 'glossary'),
        icon: BookOpen,
        section: 'Admin' as const,
        keywords: ['admin', 'content', 'glossary', 'terms'],
      },
      {
        id: 'admin-content-breach-feed',
        kind: 'page' as const,
        title: 'Breach Feed',
        subtitle: 'Breach feed curation and monitoring',
        pathLabel: 'Page > Admin > Content > Breach Feed',
        url: getAdminTabUrl('content', 'breach-feed'),
        icon: BookOpen,
        section: 'Admin' as const,
        keywords: ['admin', 'content', 'breach feed', 'news'],
      },
      {
        id: 'admin-content-advisories',
        kind: 'page' as const,
        title: 'Advisories',
        subtitle: 'Security advisories and their email consoles',
        pathLabel: 'Page > Admin > Content > Advisories',
        url: getAdminTabUrl('content', 'advisories'),
        icon: Megaphone,
        section: 'Admin' as const,
        keywords: ['admin', 'content', 'advisories', 'advisory', 'runtime', 'php2shell', 'email console'],
      },
      {
        id: 'admin-content-dependencies',
        kind: 'page' as const,
        title: 'Dependencies',
        subtitle: 'Dependency mapping documentation',
        pathLabel: 'Page > Admin > Content > Dependencies',
        url: getAdminTabUrl('content', 'dependencies'),
        icon: BookOpen,
        section: 'Admin' as const,
        keywords: ['admin', 'content', 'dependencies', 'documentation'],
      },
      {
        id: 'admin-platform-docs',
        kind: 'page' as const,
        title: 'Documentation',
        subtitle: 'Platform docs and operational guides',
        pathLabel: 'Page > Admin > Platform > Documentation',
        url: getAdminTabUrl('platform', 'docs'),
        icon: Settings,
        section: 'Admin' as const,
        keywords: ['admin', 'platform', 'documentation', 'docs'],
      },
      {
        id: 'admin-platform-cron',
        kind: 'page' as const,
        title: 'Cron',
        subtitle: 'Cron monitoring and job visibility',
        pathLabel: 'Page > Admin > Platform > Cron',
        url: getAdminTabUrl('platform', 'cron'),
        icon: Settings,
        section: 'Admin' as const,
        keywords: ['admin', 'platform', 'cron', 'jobs'],
      },
      {
        id: 'admin-platform-api-keys',
        kind: 'page' as const,
        title: 'API Keys',
        subtitle: 'Vault-backed provider secrets',
        pathLabel: 'Page > Admin > Platform > API Keys',
        url: getAdminTabUrl('platform', 'api-keys'),
        icon: Settings,
        section: 'Admin' as const,
        keywords: ['admin', 'platform', 'integrations', 'api keys', 'keys', 'vault'],
      },
      {
        id: 'admin-platform-api-budgets',
        kind: 'page' as const,
        title: 'API Quotas',
        subtitle: 'Quota controls for third-party API usage',
        pathLabel: 'Page > Admin > Platform > API Quotas',
        url: getAdminTabUrl('platform', 'api-budgets'),
        icon: Settings,
        section: 'Admin' as const,
        keywords: ['admin', 'platform', 'budgets', 'quota', 'rate limits', 'api quotas'],
      },
      {
        id: 'admin-platform-security',
        kind: 'page' as const,
        title: 'Security',
        subtitle: 'Security settings and platform hardening',
        pathLabel: 'Page > Admin > Platform > Security',
        url: getAdminTabUrl('platform', 'security'),
        icon: Settings,
        section: 'Admin' as const,
        keywords: ['admin', 'platform', 'security', 'hardening'],
      },
      {
        id: 'admin-platform-feature-flags',
        kind: 'page' as const,
        title: 'Feature Flags',
        subtitle: 'Feature flag rollout and control',
        pathLabel: 'Page > Admin > Platform > Feature Flags',
        url: getAdminTabUrl('platform', 'feature-flags'),
        icon: Settings,
        section: 'Admin' as const,
        keywords: ['admin', 'platform', 'feature flags', 'flags'],
      },
      {
        id: 'admin-platform-notification-coverage',
        kind: 'page' as const,
        title: 'Notification Coverage',
        subtitle: 'Declared notification and alert coverage inventory',
        pathLabel: 'Page > Admin > Platform > Notification Coverage',
        url: getAdminTabUrl('platform', 'notification-coverage'),
        icon: Settings,
        section: 'Admin' as const,
        keywords: ['admin', 'platform', 'notification coverage', 'alert coverage', 'notification registry'],
      },
      {
        id: 'admin-platform-audit-coverage',
        kind: 'page' as const,
        title: 'Audit Coverage',
        subtitle: 'Declared audit coverage inventory and recent observation',
        pathLabel: 'Page > Admin > Platform > Audit Coverage',
        url: getAdminTabUrl('platform', 'audit-coverage'),
        icon: Settings,
        section: 'Admin' as const,
        keywords: ['admin', 'platform', 'audit coverage', 'audit registry', 'audit heatmap'],
      },
      {
        id: 'admin-platform-audit-log',
        kind: 'page' as const,
        title: 'Audit Log',
        subtitle: 'Canonical platform and organisation audit events',
        pathLabel: 'Page > Admin > Platform > Audit Log',
        url: getAdminTabUrl('platform', 'audit-log'),
        icon: Settings,
        section: 'Admin' as const,
        keywords: ['admin', 'platform', 'audit log', 'audit history', 'activity log'],
      },
      {
        id: 'customer-view',
        kind: 'action' as const,
        title: CUSTOMER_PROFILE_LABEL,
        subtitle: 'Return to the customer workspace shell',
        pathLabel: `Action > ${ADMIN_PROFILE_LABEL} > ${CUSTOMER_PROFILE_LABEL}`,
        url: '/dashboard',
        icon: ArrowLeftRight,
        section: 'Actions' as const,
        keywords: ['customer view', 'workspace', 'switch back'],
        navigateAfterSelect: true,
        onSelect: () => {
          beginPortalSwitch('workspace');
          navigate('/dashboard');
        },
      },
    ],
    [adminNavItems, navigate],
  );

  const userInitials = getProfileInitials(resolvedProfile, user?.email);
  const activeWorkspaceName = organization?.name || 'Platform administration';
  const activeWorkspaceCopyValue = organization ? `${activeWorkspaceName} (${organization.id})` : null;
  const activeProfileName = getProfileDisplayName(resolvedProfile, user?.email) || 'Admin User';
  const activeProfileCopyValue = activeProfileName && user?.id && user?.email
    ? `${activeProfileName} (${user.id}) - ${user.email}`
    : null;
  const collapsedNavItemClass = collapsed ? 'mx-auto !size-10 !gap-0 !px-3 !py-2.5 rounded-xl' : '';

  const handleCopyActiveWorkspace = async () => {
    if (!activeWorkspaceCopyValue) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        formatClipboardJson({
          type: 'workspace',
          name: organization?.name ?? null,
          id: organization?.id ?? null,
          value: activeWorkspaceCopyValue,
        }),
      );
      toast({ title: 'Workspace JSON copied to clipboard' });
    } catch (error) {
      toast({
        title: 'Could not copy details',
        description: 'Clipboard access was unavailable.',
        variant: 'destructive',
      });
      console.error('Could not copy admin workspace info', error);
    }
  };

  const handleCopyActiveProfile = async () => {
    if (!activeProfileCopyValue) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        formatClipboardJson({
          type: 'profile',
          name: activeProfileName,
          id: user?.id ?? null,
          email: user?.email ?? null,
          value: activeProfileCopyValue,
        }),
      );
      toast({ title: 'Profile JSON copied to clipboard' });
    } catch (error) {
      toast({
        title: 'Could not copy details',
        description: 'Clipboard access was unavailable.',
        variant: 'destructive',
      });
      console.error('Could not copy admin profile info', error);
    }
  };

  const handleCopyVersion = async () => {
    try {
      await navigator.clipboard.writeText(formatBuildInfoForClipboard());
      toast({ title: 'Build info copied to clipboard' });
    } catch (error) {
      console.error('Could not copy admin build info', error);
      toast({
        title: 'Could not copy build info',
        description: 'Clipboard access was unavailable.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Sidebar
      collapsible="icon"
      className="border-r border-rose-700/30 !bg-gradient-to-b !from-rose-950 !via-red-900 !to-orange-900 [&_[data-sidebar=sidebar]]:relative [&_[data-sidebar=sidebar]]:overflow-hidden [&_[data-sidebar=sidebar]]:bg-gradient-to-b [&_[data-sidebar=sidebar]]:from-rose-950 [&_[data-sidebar=sidebar]]:via-red-900 [&_[data-sidebar=sidebar]]:to-orange-900"
    >
      <SurfaceSidebarBackdrop tone="admin" collapsed={collapsed} idPrefix="admin-sidebar-depth" testId="admin-sidebar-depth-layer" />

      <SurfaceSidebarHeader
        tone="admin"
        commandPalette={<AppCommandPalette collapsed={collapsed} items={commandPaletteItems} isMSP={false} tone="admin" enableAdminListingSearch />}
      >
        <DropdownMenu>
          <div className={`flex items-center gap-3 ${collapsed ? 'justify-center' : ''}`} data-testid="admin-branding-row">
            {!collapsed && (
                <SurfaceSidebarBrandButton
                  tone="admin"
                  ariaLabel="Open admin dashboard home"
                  onClick={() => navigate('/admin')}
                >
                  <SurfaceSidebarBrandPanel tone="admin">
                    <PlatformLogo variant="white" className="h-6 w-6" />
                  </SurfaceSidebarBrandPanel>
                </SurfaceSidebarBrandButton>
            )}
            <SurfaceSidebarSwitcherControl
              collapsed={collapsed}
            >
              <DropdownMenuTrigger asChild>
                <SurfaceSidebarSwitcherButton
                  tone="admin"
                  collapsed={collapsed}
                  ariaLabel="Open admin portal switch menu"
                  title={ADMIN_PROFILE_LABEL}
                  subtitle={activeWorkspaceName}
                  trailingAccessory={!collapsed && activeWorkspaceCopyValue ? (
                    <SurfaceSidebarCopyButton
                      tone="admin"
                      ariaLabel="Copy active workspace email"
                      onClick={() => void handleCopyActiveWorkspace()}
                      className="shrink-0"
                    />
                  ) : undefined}
                />
              </DropdownMenuTrigger>
            </SurfaceSidebarSwitcherControl>
          </div>
          <DropdownMenuContent align="start" className="z-50 w-56 bg-popover">
            <DropdownMenuItem onClick={() => navigate('/admin')} className="text-rose-600 dark:text-rose-400">
              <Shield className="mr-2 h-4 w-4" />
              {ADMIN_PROFILE_LABEL}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                beginPortalSwitch('workspace');
                navigate('/dashboard');
              }}
              className="text-purple-600 dark:text-purple-400"
            >
              <PlatformLogo variant="purple" className="mr-2 h-4 w-4" />
              {CUSTOMER_PROFILE_LABEL}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setSuperAdminDebugOverlayEnabled(!debugOverlayEnabled)}>
              <Activity className="mr-2 h-4 w-4" />
              {debugOverlayEnabled ? 'Hide Live Statistics' : 'Show Live Statistics'}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SurfaceSidebarHeader>

      <SurfaceSidebarContent>
        <div data-testid="admin-sidebar-main-nav" className={collapsed ? 'flex-1 min-h-0 overflow-hidden' : 'flex-1 min-h-0 overflow-y-auto'}>
          <SurfaceSidebarSection tone="admin" className="pt-3">
              <SidebarMenu>
                {adminNavItems.map((item) => (
                  <SurfaceSidebarNavItemLink
                    key={item.title}
                    tone="admin"
                    to={item.url}
                    title={item.title}
                    icon={item.icon}
                    collapsed={collapsed}
                    collapsedNavItemClass={collapsedNavItemClass}
                    active={item.tab === activeTab}
                  />
                ))}
              </SidebarMenu>
          </SurfaceSidebarSection>
        </div>

        {storybookCatalogAvailable ? (
          <div
            data-testid="admin-sidebar-tools-section"
            className={collapsed ? 'flex-shrink-0 border-t border-rose-500/20 pt-2' : 'flex-shrink-0 border-t border-rose-500/20'}
          >
            <SurfaceSidebarSection tone="admin" label="Tools" className="py-2">
              <SidebarMenu>
                <SurfaceSidebarNavItemLink
                  tone="admin"
                  to="/admin/storybook"
                  title="Storybook"
                  icon={Palette}
                  collapsed={collapsed}
                  collapsedNavItemClass={collapsedNavItemClass}
                  tooltip="Component catalog (super-admin only)"
                />
              </SidebarMenu>
            </SurfaceSidebarSection>
          </div>
        ) : null}

        <div
          data-testid="admin-sidebar-bottom-section"
          className={collapsed ? 'flex-shrink-0 border-t border-rose-500/20 pt-2' : 'flex-shrink-0 border-t border-rose-500/20'}
        >
          <SurfaceSidebarSection tone="admin" label={SWITCH_PROFILE_LABEL} className="py-2">
              <SidebarMenu>
                <SurfaceSidebarPortalLink
                  tone="admin"
                  to="/dashboard"
                  title={MSP_PROFILE_LABEL}
                  icon={ArrowLeftRight}
                  collapsed={collapsed}
                  collapsedNavItemClass={collapsedNavItemClass}
                  onClick={() => beginPortalSwitch('workspace')}
                  accent="purple"
                />
                {supplierPortalUrl ? (
                  <SurfaceSidebarPortalLink
                    tone="admin"
                    to={supplierPortalUrl}
                    title={supplierPortalLabel}
                    icon={Shield}
                    collapsed={collapsed}
                    collapsedNavItemClass={collapsedNavItemClass}
                    onClick={() => beginPortalSwitch('vendor')}
                    accent="emerald"
                  />
                ) : null}
              </SidebarMenu>
          </SurfaceSidebarSection>
        </div>
      </SurfaceSidebarContent>

      <SurfaceSidebarFooter tone="admin" bordered={!collapsed}>
        <SurfaceSidebarProfileMenu
          tone="admin"
          collapsed={collapsed}
          avatarLabel="Profile picture"
          avatarSrc={profile?.avatar_url || undefined}
          initials={userInitials}
          name={activeProfileName}
          roleLabel="Super Admin"
          onAvatarClick={() => navigate('/settings?tab=profile')}
          avatarAriaLabel="Open admin profile settings"
          copyButton={!collapsed && activeProfileCopyValue ? {
            ariaLabel: 'Copy active profile email',
            onClick: () => void handleCopyActiveProfile(),
          } : undefined}
        >
            <ThemeModeMenu />
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings?tab=profile')}>
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleCopyVersion}>
              <Copy className="h-4 w-4 mr-2" />
              <div className="flex min-w-0 flex-col">
                <span className="whitespace-nowrap text-[11px] leading-none">{formatVersionForDisplay(appBuildInfo.version)}</span>
                <span className="whitespace-nowrap text-[10px] leading-tight text-muted-foreground">
                  Built {formatBuiltAtForDisplay(appBuildInfo.builtAtUtc)}
                </span>
              </div>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
        </SurfaceSidebarProfileMenu>
      </SurfaceSidebarFooter>
    </Sidebar>
  );
}
